import { CalendarPopoverActionTypes, CalendarPopoverOpenPayload } from 'actions/CalendarActions';
import {
  CalendarRescheduleMode,
  CalendarSelectionMode,
  CalendarState,
} from 'constants/CalendarState';
import { ElaboratedTicketGroup, dashboard } from 'types';
import { SelectedTimelineItem } from 'types/tableBasedInventoryRangeVariants';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { TicketGroupsByDateTime } from 'selectors/ticketGroupsByDateTimeSelector';
import { computeSavedEntityState } from './utils/computeSavedEntityState';
import { core, payloadToJSON, toJSON, toJSONArray } from '@tock/coretypes';
import {
  every,
  find,
  forEach,
  isEmpty,
  keyBy,
  map,
  matches,
  omit,
  reduce,
  size,
  sortBy,
  zip,
} from 'es-toolkit/compat';
import {
  getCalendarItemFromListByOption,
  getCalendarItemFromTicketGroupsByOption,
  getSelectedInventoryItemId,
} from 'utils/calendarUtils';
import { isEqual } from 'es-toolkit';
import { logger } from '@tock/logging';
import { oc } from 'ts-optchain';
import { todayBusinessDay } from '@tock/shared/utils/time';
import AppConstants from 'constants/AppConstants';
import TableTypeConstants from 'constants/TableTypeConstants';
import calendarItems, {
  CalendarItem,
  CalendarItemMeta,
  CalendarItemStateEnum,
} from 'utils/calendarItems';

export interface CalendarItemSelections {
  [calendarItemId: string]: CalendarItem;
}

export type SelectedInventoryItemId =
  | SelectedAvailable
  | SelectedHeld
  | SelectedLocked
  | SelectedCommunal
  | SelectedSold
  | SelectedTimeFlexible
  | SelectedWalkIn;

interface SelectedAvailable {
  type: CalendarItemStateEnum.AVAILABLE;
  calendarItemId: string;
}

interface SelectedHeld {
  type: CalendarItemStateEnum.HELD;
  ticketSubsetId: number;
}

interface SelectedLocked {
  type: CalendarItemStateEnum.LOCKED;
  ticketSubsetId: number;
}

interface SelectedCommunal {
  type: CalendarItemStateEnum.COMMUNAL;
  calendarItemId: string;
}

interface SelectedSold {
  type: CalendarItemStateEnum.SOLD;
  originalPurchaseId: number;
}

interface SelectedTimeFlexible {
  type: CalendarItemStateEnum.TIME_FLEXIBLE;
  calendarItemId: string;
}

interface SelectedWalkIn {
  type: 'WALK_IN';
  partyId: number;
}

export interface SavedState {
  calendar: ElaboratedTicketGroup[];
  purchases: NumericDictionary<core.PastPurchase>;
  visitsByWalkinId: NumericDictionary<core.Visit>;
  waitlists: NumericDictionary<dashboard.CalendarWaitlist>;
}

export interface State {
  bookingModalVisible: boolean;
  calendar: ElaboratedTicketGroup[];
  purchases: NumericDictionary<core.PastPurchase>;
  visitsByWalkinId: NumericDictionary<core.Visit>;
  waitlists: NumericDictionary<dashboard.CalendarWaitlist>;
  savedStateKeys: string[];
  savedState: {
    [date: string]: SavedState;
  };
  calendarIsInitialized: boolean;
  calendarItemRequestInProgress: boolean;
  calendarItemSelections: CalendarItemSelections;
  calendarItemToHold?: CalendarItem;
  calendarItemToMove?: CalendarItem;
  calendarState: CalendarState;
  dateTimeToMove?: string;
  focusedTimeSpan?: { startTime: string; duration: number };
  isSelectedCalendarItemFlex: boolean;
  monthCalendar: Dictionary<dashboard.CalendarDaySummary>;
  nameFilter: string;
  popoverType?: CalendarPopoverOpenPayload;
  requestInProgress: boolean;
  rescheduleMode?: CalendarRescheduleMode;
  selectedCalendarItemMeta?: CalendarItemMeta;
  selectedInventoryItemId?: SelectedInventoryItemId;
  selectedTableId?: string;
  selectedTableIdCalendarSquare?: number;
  selectedTableIdsToBook?: number[];
  selectionMode?: CalendarSelectionMode;
  sendingTicketGroupToWaitlist?: core.TicketGroup;
  showActionsModal: boolean;
  showTicketSubsetHoldModal: boolean;
  tainted: boolean;
  textFilter?: string;
  ticketGroupToEdit?: core.TicketGroup;

  // New Timeline API
  selectedTimelineItem: SelectedTimelineItem | null;
}

export const initialState: State = {
  bookingModalVisible: false,
  calendar: [],
  purchases: {},
  waitlists: {},
  visitsByWalkinId: {},
  savedStateKeys: [],
  savedState: {},
  calendarIsInitialized: false,
  calendarItemRequestInProgress: false,
  calendarItemSelections: {},
  calendarItemToHold: undefined,
  calendarItemToMove: undefined,
  calendarState: CalendarState.DEFAULT,
  dateTimeToMove: undefined,
  focusedTimeSpan: undefined,
  isSelectedCalendarItemFlex: false,
  monthCalendar: {},
  nameFilter: '',
  popoverType: undefined,
  requestInProgress: false,
  rescheduleMode: undefined,
  selectedInventoryItemId: undefined,
  selectedCalendarItemMeta: undefined,
  selectedTableId: undefined,
  selectedTableIdCalendarSquare: undefined,
  selectedTableIdsToBook: undefined,
  selectedTimelineItem: null,
  sendingTicketGroupToWaitlist: undefined,
  showActionsModal: false,
  showTicketSubsetHoldModal: false,
  tainted: false,
  textFilter: undefined,
  ticketGroupToEdit: undefined,
};

/*
 * Function that modifies a ticket groups from the server response so that
 * it adheres to the model used in the calendar.
 */
export function elaborateTicketGroup(
  ticketGroup: core.TicketGroup,
  existingTicketGroup?: core.TicketGroup
): ElaboratedTicketGroup {
  const newTicketGroup = (
    existingTicketGroup && ticketGroup.includesOnlyCommonUpdates
      ? {
          ...existingTicketGroup,
          purchaseDetail: ticketGroup.purchaseDetail,
          ticketSubset: ticketGroup.ticketSubset,
          block: ticketGroup.block,
          includesOnlyCommonUpdates: ticketGroup.includesOnlyCommonUpdates,
          doesNotIncludeAllPurchases: ticketGroup.doesNotIncludeAllPurchases,
        }
      : { ...ticketGroup }
  ) as ElaboratedTicketGroup;
  newTicketGroup.type = sortBy(newTicketGroup.type, ['id']);
  newTicketGroup.option = sortBy(newTicketGroup.option, ['id']);
  newTicketGroup.fee = sortBy(newTicketGroup.fee, (feePrice) => feePrice.price?.id);

  newTicketGroup.purchaseDetail = handleIncrementalPurchaseUpdates(
    newTicketGroup,
    existingTicketGroup
  );

  newTicketGroup.calendarItems = calendarItems.computeCalendarItemsForTicketGroup(newTicketGroup);
  forEach(newTicketGroup.purchaseDetail as core.PastPurchase[], (details) => {
    if (details.question) {
      details.question.sort((question1, question2) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return question1.ordering! - question2.ordering!;
      });
    }
  });
  return newTicketGroup;
}
/*
 * Function to sort ticket groups by state, type and max size.
 */
function sortTicketGroups(ticketGroups: ElaboratedTicketGroup[]): ElaboratedTicketGroup[] {
  return sortBy(ticketGroups, [
    'state',
    (tg: ElaboratedTicketGroup) => {
      if (isEmpty(tg.purchaseDetail)) {
        return tg.type && tg.type.length > 0 ? tg.type[0].id : -1;
      } else {
        return tg.purchaseDetail[0].ticketTypeId;
      }
    },
    (tg: ElaboratedTicketGroup) => {
      return size(tg.purchaseDetail);
    },
    (tg: ElaboratedTicketGroup) => {
      return isEmpty(tg.purchaseDetail) ? -1 : tg.purchaseDetail[0].ticketCount;
    },
    'maxSize',
  ]);
}

/**
 * Most of our code assumes that when we send down a ticket group, we want to
 * replace the whole ticket group. That requires us to send down _all_ purchases
 * for a ticket group even if we're just changing or removing one purchase.
 *
 * This function allows us to get more surgical. We can pass in a ticket group
 * that just includes _updated_ purchases and use those updates to replace (or
 * add to) individual purchases from the existing ticket group object. This
 * allows us to create and edit individual purchases without sending down an
 * entire ticket group.
 *
 * We might also receive some "dummy" purchase objects that just have an id and
 * an originalPurchaseId. In this case, we want to fall back to the matching
 * purchase from the existing ticket group object.
 *
 * @param newTicketGroup The ticket group from the latest update
 * @param existingTicketGroup If it exists, the client's existing representation
 *   of the same ticket group before the update
 * @returns The reconciled list of purchases in this ticket group, potentially
 *   combining purchases from `newTicketGroup` and `existingTicketGroup`
 */
function handleIncrementalPurchaseUpdates(
  newTicketGroup: core.TicketGroup,
  existingTicketGroup?: core.TicketGroup
): core.IPastPurchase[] {
  if (
    !newTicketGroup.doesNotIncludeAllPurchases &&
    !newTicketGroup.purchaseDetail.some((purchase) => purchase.includesOnlyIds)
  ) {
    // If there are no booleans stating that anything is specially encoded, we
    // can short-circuit and return the raw purchases that were sent down. This
    // block is simply a performance optimization and should have no effect on
    // what's actually returned.
    return newTicketGroup.purchaseDetail;
  }

  const existingPurchasesMap: NumericDictionary<core.IPastPurchase> = {};
  const updatedPurchasesMap: NumericDictionary<core.IPastPurchase> = {};
  const allOriginalPurchaseIds: Set<number> = new Set();

  const existingPurchases = existingTicketGroup?.purchaseDetail ?? [];
  existingPurchases.forEach((purchase) => {
    if (purchase.originalPurchaseId) {
      existingPurchasesMap[purchase.originalPurchaseId] = purchase;
      allOriginalPurchaseIds.add(purchase.originalPurchaseId);
    }
  });

  const updatedPurchases = newTicketGroup.purchaseDetail;
  updatedPurchases.forEach((purchase) => {
    if (purchase.originalPurchaseId) {
      updatedPurchasesMap[purchase.originalPurchaseId] = purchase;
      allOriginalPurchaseIds.add(purchase.originalPurchaseId);
    }
  });

  if (newTicketGroup.doesNotIncludeAllPurchases) {
    // If the encoded ticket group does not include all purchases, we know that
    // only modified purchases (including newly added purchases) have been sent
    // down. We'll use all the purchases from the newly encoded ticket group,
    // and fall back to the existing ticket group for all the remaining
    // purchases. This flow cannot be used to remove a purchase from a ticket
    // group.
    return [...allOriginalPurchaseIds].map(
      (originalPurchaseId) =>
        updatedPurchasesMap[originalPurchaseId] ?? existingPurchasesMap[originalPurchaseId]
    );
  }

  // If we're here, we've been sent the full set of purchases for this ticket
  // group. However, some of them (or all of them) might just include IDs and
  // don't actually have any details. For these purchases, we'll fall back to
  // the purchase details we already have.
  return updatedPurchases.map((updatedPurchase) =>
    updatedPurchase.includesOnlyIds && updatedPurchase.originalPurchaseId
      ? { ...existingPurchasesMap[updatedPurchase.originalPurchaseId], id: updatedPurchase.id }
      : updatedPurchase
  );
}

/**
 *  get related calendar items to either be removed or added
 */
export function getRelatedCalendarItems(
  timeSlot: ElaboratedTicketGroup[],
  calendarItem: CalendarItem
) {
  return reduce(
    timeSlot,
    (acc: { [id: string]: CalendarItem }, item: ElaboratedTicketGroup) => {
      if (!isEmpty(item.calendarItems)) {
        forEach(item.calendarItems, (calItem: CalendarItem) => {
          if (calItem.numBookable > 0) {
            if (calItem.ticketGroup.id === calendarItem.ticketGroup.id) {
              acc[calItem.id] = calItem;
            }
            const sameKind =
              calItem.ticketGroup.tableVariety === TableTypeConstants.COMMUNAL_TABLE ||
              matches({
                dateTime: calendarItem.ticketGroup.dateTime,
                maxSize: calendarItem.ticketGroup.maxSize,
                minPurchaseSize: calendarItem.ticketGroup.minPurchaseSize,
                maxPurchaseSize: calendarItem.ticketGroup.maxPurchaseSize,
              })(calItem.ticketGroup);

            if (
              sameKind &&
              oc(calItem.ticketGroup).type[0].id() === oc(calendarItem.ticketGroup).type[0].id()
            ) {
              acc[calItem.id] = calItem;
            }
          }
        });
      }
      return acc;
    },
    {}
  );
}

const serviceInDashboardKeys = [
  'diningTimeSeconds',
  'leftTimeSecs',
  'primaryState',
  'seatedTimeSecs',
  'secondaryState',
  'server',
  'table',
];
const otherKeysToIgnore = [
  'ownerPatron',
  'bookingNote',
  'ownerGuestProfile',
  'dinerGuestProfile',
  'auditedBookingNote',
  'visit',
  'ticketType.question',
  'question',
];
const keysToIgnore = otherKeysToIgnore.concat(serviceInDashboardKeys);
function areTwoPurchasesEquivalentEnough(
  purchase1: core.IPastPurchase,
  purchase2: core.IPastPurchase
) {
  return isEqual(omit(purchase1, keysToIgnore), omit(purchase2, keysToIgnore));
}

function updateCalendarItemPurchase(
  purchase: core.PastPurchase,
  elaboratedTicketGroups: ElaboratedTicketGroup[]
) {
  for (const elaboratedTicketGroup of elaboratedTicketGroups) {
    for (const calendarItem of elaboratedTicketGroup.calendarItems) {
      if (calendarItem?.purchase?.id === purchase.id) {
        calendarItem.purchase = purchase;
        break;
      }
    }
  }
}

function updatePurchase(
  purchase: core.PastPurchase,
  purchases: NumericDictionary<core.PastPurchase>
) {
  if (!purchase.originalPurchaseId) {
    return;
  }
  if (purchase.transferred) {
    delete purchases[purchase.originalPurchaseId];
    return;
  }
  purchases[purchase.originalPurchaseId] = purchase as core.PastPurchase;
}

function replaceCalendarItem(ticketGroups: ElaboratedTicketGroup[], calendarItem?: CalendarItem) {
  if (!calendarItem) {
    return undefined;
  }
  return find(
    oc(
      find(ticketGroups, (ticketGroup) => ticketGroup.id === calendarItem.ticketGroup.id)
    ).calendarItems(),
    (newCalendarItem: CalendarItem) => {
      return newCalendarItem.id === calendarItem.id;
    }
  );
}

export default function calendarReducer(
  state: State = initialState,
  action: BaseTockAction
): State {
  const { payload, context } = action;
  switch (action.type) {
    case AppConstants.CALENDAR_SELECT_TAB: {
      return {
        ...state,
        nameFilter: '',
      };
    }
    case AppConstants.SET_SELECTED_TABLE_IDS_TO_BOOK: {
      return {
        ...state,
        selectedTableIdsToBook: payload,
      };
    }
    case AppConstants.CLEAR_SELECTED_TABLE_IDS_TO_BOOK: {
      return {
        ...state,
        selectedTableIdsToBook: undefined,
      };
    }
    case AppConstants.MONTH_CALENDAR_FETCH_ALL_DONE: {
      return {
        ...state,
        monthCalendar: keyBy(
          toJSONArray<dashboard.CalendarDaySummary>(payload),
          (summary) => summary.date
        ),
        requestInProgress: false,
      };
    }
    case SharedAppConstants.GUEST_PROFILE_FETCH_PATRON_PURCHASE_HISTORY_DONE: {
      const purchases = { ...state.purchases };
      const elaboratedTicketGroups = [...state.calendar];

      const purchasesFromHistory = payloadToJSON<core.PastPurchase[]>(payload);
      purchasesFromHistory.forEach((purchase) => {
        updatePurchase(purchase, purchases);
        updateCalendarItemPurchase(purchase, elaboratedTicketGroups);
      });

      return {
        ...state,
        calendar: elaboratedTicketGroups,
        purchases,
      };
    }
    case AppConstants.CALENDAR_ENTITY_UPDATE: {
      const changeUpdates = payload as dashboard.CalendarDayEntityUpdates;
      if (oc(changeUpdates).update[0].type() === 'INIT') {
        const waitlists: NumericDictionary<dashboard.CalendarWaitlist> = {};
        const purchases: NumericDictionary<core.PastPurchase> = {};
        const visitsByWalkinId: NumericDictionary<core.Visit> = {};
        const elaboratedTicketGroups = sortTicketGroups(
          map(oc(changeUpdates).update[0].ticketGroup() as core.TicketGroup[], (ticketGroup) =>
            elaborateTicketGroup(ticketGroup)
          )
        );

        const calendarItemToHold = replaceCalendarItem(
          elaboratedTicketGroups,
          state.calendarItemToHold
        );
        // Only replace calendar items if the slots are from a flex business. If it's a
        // slot venue then this slot will not exist in the ticketGroups. Impeding customers
        // from moving slots from one day to another.
        const calendarItemToMove = state.calendarItemToMove?.getIsFlex()
          ? replaceCalendarItem(elaboratedTicketGroups, state.calendarItemToMove)
          : state.calendarItemToMove;

        forEach(oc(changeUpdates).update[0].ticketGroup(), (ticketGroup) => {
          forEach(ticketGroup.purchaseDetail as core.PastPurchase[], (purchase) => {
            updatePurchase(purchase, purchases);
          });
        });
        forEach(oc(changeUpdates).update[0].purchase() as core.PastPurchase[], (purchase) => {
          updatePurchase(purchase, purchases);
        });
        forEach(oc(changeUpdates).update[0].walkin() as dashboard.WalkIn[], (walkIn) => {
          const walkInId = oc(walkIn).visit.walkInId();
          if (walkInId) {
            visitsByWalkinId[walkInId] = walkIn.visit as core.Visit;
          }
        });
        forEach(
          oc(changeUpdates).update[0].waitlist() as dashboard.CalendarWaitlist[],
          (waitlist) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const id = oc(waitlist).id()!;
            waitlists[id] = waitlist as dashboard.CalendarWaitlist;
          }
        );
        return {
          ...state,
          calendar: elaboratedTicketGroups,
          calendarIsInitialized: true,
          calendarItemToHold,
          calendarItemToMove,
          purchases,
          visitsByWalkinId,
          waitlists,
          tainted: false,
          requestInProgress: false,
        };
      } else {
        const waitlists: NumericDictionary<dashboard.CalendarWaitlist> = state.waitlists;
        const purchases: NumericDictionary<core.PastPurchase> = state.purchases;
        const visitsByWalkinId: NumericDictionary<core.Visit> = state.visitsByWalkinId;
        const existingTicketGroups = keyBy(state.calendar, (ticketGroup) => ticketGroup.id);
        forEach(changeUpdates.update, (update) => {
          if (update.type === 'REPLACE') {
            forEach(update.ticketGroup as core.TicketGroup[], (ticketGroup) => {
              existingTicketGroups[ticketGroup.id] = elaborateTicketGroup(
                ticketGroup,
                existingTicketGroups[ticketGroup.id]
              );
              forEach(ticketGroup.purchaseDetail as core.PastPurchase[], (purchase) => {
                updatePurchase(purchase, purchases);
              });
            });

            forEach(update.purchase as core.PastPurchase[], (purchase) => {
              updatePurchase(purchase, purchases);
            });

            forEach(oc(changeUpdates).update[0].walkin() as dashboard.WalkIn[], (walkIn) => {
              const walkInId = oc(walkIn).visit.walkInId();
              if (walkInId) {
                visitsByWalkinId[walkInId] = walkIn.visit as core.Visit;
              }
            });

            forEach(
              oc(changeUpdates).update[0].waitlist() as dashboard.CalendarWaitlist[],
              (waitlist) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const id = oc(waitlist).id()!;
                waitlists[id] = waitlist as dashboard.CalendarWaitlist;
              }
            );
          } else if (update.type === 'REMOVE') {
            forEach(update.waitlist, (waitlist) => {
              if (waitlist.id) {
                delete waitlists[waitlist.id];
              }
            });

            forEach(update.ticketGroup, (ticketGroup) => {
              if (ticketGroup.id) {
                delete existingTicketGroups[ticketGroup.id];
              }
            });
          } else {
            logger.error('Encountered unsupported update type');
          }
        });

        const elaboratedTicketGroups = sortTicketGroups(Object.values(existingTicketGroups));

        let newSelectedCalendarItem = getCalendarItemFromTicketGroupsByOption(
          elaboratedTicketGroups,
          state.selectedInventoryItemId
        );

        if (!newSelectedCalendarItem) {
          const prevSelectedCalendarItem = getCalendarItemFromTicketGroupsByOption(
            state.calendar,
            state.selectedInventoryItemId
          );
          newSelectedCalendarItem = replaceCalendarItem(
            elaboratedTicketGroups,
            prevSelectedCalendarItem
          );
        }

        const newSelectedInventoryItemId = getSelectedInventoryItemId(newSelectedCalendarItem);

        const calendarItemToHold = replaceCalendarItem(
          elaboratedTicketGroups,
          state.calendarItemToHold
        );
        // Only replace calendar items if the slots are from a flex business. If it's a
        // slot venue then this slot will not exist in the ticketGroups. Impeding customers
        // from moving slots from one day to another.
        const calendarItemToMove = state.calendarItemToMove?.getIsFlex()
          ? replaceCalendarItem(elaboratedTicketGroups, state.calendarItemToMove)
          : state.calendarItemToMove;

        return {
          ...state,
          tainted: false,
          calendar: elaboratedTicketGroups,
          calendarItemToHold,
          calendarItemToMove,
          purchases: {
            ...purchases,
          },
          visitsByWalkinId: {
            ...visitsByWalkinId,
          },
          waitlists: {
            ...waitlists,
          },
          selectedInventoryItemId: newSelectedInventoryItemId,
        };
      }
    }
    case AppConstants.CALENDAR_ENTITY_SAVE_DATE: {
      const stateToSave: SavedState = {
        calendar: state.calendar,
        purchases: state.purchases,
        visitsByWalkinId: state.visitsByWalkinId,
        waitlists: state.waitlists,
      };
      const newSavedState: Dictionary<SavedState> = computeSavedEntityState<SavedState>({
        newDate: payload,
        stateToSave,
        savedState: state.savedState,
        todayString: todayBusinessDay().toString(),
      });

      return {
        ...state,
        savedState: newSavedState,
      };
    }
    case AppConstants.CALENDAR_ENTITY_RESTORE_DATE: {
      const restoredState = state.savedState[payload];
      if (!restoredState) {
        return {
          ...state,
          calendar: initialState.calendar,
          purchases: initialState.purchases,
          visitsByWalkinId: initialState.visitsByWalkinId,
          waitlists: initialState.waitlists,
          calendarIsInitialized: false,
          tainted: true,
          requestInProgress: true,
        };
      }
      return {
        ...state,
        calendar: restoredState.calendar,
        purchases: restoredState.purchases,
        visitsByWalkinId: restoredState.visitsByWalkinId,
        waitlists: restoredState.waitlists,
        calendarIsInitialized: true,
        tainted: false,
        requestInProgress: false,
      };
    }
    case AppConstants.CALENDAR_CLEAR_SELECTED_CALENDAR_ITEM: {
      return {
        ...state,
        calendarItemSelections: {},
        selectedCalendarItemMeta: undefined,
        selectedInventoryItemId: undefined,
        isSelectedCalendarItemFlex: false,
      };
    }
    case AppConstants.CALENDAR_SPECIFY_SELECTED_CALENDAR_ITEM: {
      return {
        ...state,
        selectedInventoryItemId: payload,
      };
    }
    case AppConstants.CALENDAR_SET_BOOKING_MODAL_VISIBLE: {
      return {
        ...state,
        bookingModalVisible: payload,
      };
    }
    case AppConstants.CALENDAR_SET_SHOW_TICKET_SUBSET_HOLD_MODAL: {
      return {
        ...state,
        showTicketSubsetHoldModal: payload,
      };
    }
    case AppConstants.CALENDAR_SET_SHOW_ACTIONS_MODAL: {
      return {
        ...state,
        showActionsModal: payload,
      };
    }
    case AppConstants.CALENDAR_SET_TEXT_FILTER: {
      return {
        ...state,
        textFilter: payload,
      };
    }
    case AppConstants.CALENDAR_SET_STATE: {
      return {
        ...state,
        calendarState: payload,
      };
    }
    case AppConstants.CALENDAR_SET_SELECTION_MODE: {
      return {
        ...state,
        selectionMode: payload,
      };
    }
    case AppConstants.CALENDAR_SET_RESCHEDULE_MODE: {
      return {
        ...state,
        rescheduleMode: payload,
      };
    }
    case AppConstants.CALENDAR_ITEM_ADD_SELECTION: {
      return {
        ...state,
        calendarItemSelections: {
          ...state.calendarItemSelections,
          [payload.id]: payload,
        },
      };
    }
    case AppConstants.CALENDAR_ITEM_REMOVE_SELECTION: {
      const removedItems = {
        ...state.calendarItemSelections,
      };
      delete removedItems[payload.id];
      return {
        ...state,
        calendarItemSelections: removedItems,
      };
    }

    /**
     * have to loop through and get the calendar items that were generated
     */
    case AppConstants.CALENDAR_SELECT_ALL_ITEMS: {
      const ticketGroupsByDateTime: TicketGroupsByDateTime = payload;
      return {
        ...state,
        calendarItemSelections: reduce(
          Object.keys(ticketGroupsByDateTime),
          (acc: Dictionary<CalendarItem>, key: string) => {
            const timeSlot = ticketGroupsByDateTime[key];
            forEach(timeSlot, (item) => {
              if (!isEmpty(item.calendarItems)) {
                forEach(item.calendarItems, (calendarItem) => {
                  if (calendarItem.numBookable > 0) {
                    acc[calendarItem.id] = calendarItem;
                  }
                });
              }
            });
            return acc;
          },
          {}
        ),
      };
    }

    case AppConstants.CALENDAR_ADD_RELATED_ITEMS: {
      const baseCalendarItem = payload.calendarItem;
      const ticketGroupsByDateTime: TicketGroupsByDateTime = payload.ticketGroupsByDateTime;
      const timeSlot = ticketGroupsByDateTime[baseCalendarItem.dateTime];
      const newSelections = getRelatedCalendarItems(timeSlot, baseCalendarItem);

      return {
        ...state,
        calendarItemSelections: {
          ...state.calendarItemSelections,
          ...newSelections,
        },
      };
    }

    case AppConstants.CALENDAR_REMOVE_RELATED_ITEMS: {
      const baseCalendarItem = payload.calendarItem;
      const ticketGroupsByDateTime: TicketGroupsByDateTime = payload.ticketGroupsByDateTime;
      const timeSlot = ticketGroupsByDateTime[baseCalendarItem.dateTime];
      const selectionsToRemove = getRelatedCalendarItems(timeSlot, baseCalendarItem);
      const newCalendarItemSelections = {
        ...state.calendarItemSelections,
      };
      forEach(Object.keys(selectionsToRemove), (key) => {
        delete newCalendarItemSelections[key];
      });
      return {
        ...state,
        calendarItemSelections: newCalendarItemSelections,
      };
    }

    case AppConstants.CALENDAR_DESELECT_ALL_ITEMS: {
      return {
        ...state,
        calendarItemSelections: {},
      };
    }

    case AppConstants.CALENDAR_SET_SELECTED_TABLE_ID_CALENDAR_SQUARE: {
      return {
        ...state,
        selectedTableIdCalendarSquare: payload,
      };
    }

    case AppConstants.CALENDAR_SELECT_TABLE_STATE_RANGE: {
      return {
        ...state,
        selectedTimelineItem: payload,
      };
    }

    case AppConstants.CALENDAR_CLEAR_SELECTED_TABLE_STATE_RANGE: {
      return {
        ...state,
        selectedTimelineItem: null,
      };
    }

    case AppConstants.CALENDAR_ITEM_SET_META: {
      return {
        ...state,
        selectedCalendarItemMeta: payload,
      };
    }

    case AppConstants.CALENDAR_ITEM_CLEAR_META: {
      return {
        ...state,
        selectedCalendarItemMeta: undefined,
      };
    }

    case SharedAppConstants.TICKET_GROUP_GET_DONE: {
      if (!context) {
        // Calls for getting the details of a ticket group can happen outside of setting the
        // selected item. If the context is filled in it contains the calendarItem id.
        return {
          ...state,
          calendarItemRequestInProgress: false,
        };
      }

      // If the ticket group is already a json, then assume we are in realtime calendar mode.
      // and do this fast path.
      if (!payload.id.toNumber) {
        return {
          ...state,
          selectedInventoryItemId: context,
          calendarItemRequestInProgress: false,
        };
      }

      const ticketGroup = toJSON<core.TicketGroup>(payload);
      const elaboratedTicketGroup = elaborateTicketGroup(ticketGroup);

      const selectedInventoryItemId: SelectedInventoryItemId = context;
      const selectedCalendarItem = getCalendarItemFromListByOption(
        elaboratedTicketGroup.calendarItems,
        selectedInventoryItemId
      );
      const selectedTicketGroup = oc(selectedCalendarItem).ticketGroup();

      const oldTicketGroup = oc(
        getCalendarItemFromTicketGroupsByOption(state.calendar, selectedInventoryItemId)
      ).ticketGroup();
      const currentPurchase = oc(oldTicketGroup).purchaseDetail();

      const selectedPurchase = oc(selectedTicketGroup).purchaseDetail();

      // We are only returning notes on the owner patron from the ticket group endpoint
      // so the owner patrons will not align perfectly.
      let allPurchasesEqual = true;

      // WARNING - be careful when adding new fields to purchases. It might break this logic since you have to add
      // TODO: this mechanism is to prevent bugs by making sure you have the latest state before editing a ticket group.
      if (
        (selectedPurchase && selectedPurchase.length > 0) ||
        (currentPurchase && currentPurchase.length > 0)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const zippedPurchasesEqual = map(zip(currentPurchase!, selectedPurchase!), (pair) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return areTwoPurchasesEquivalentEnough(pair[0]!, pair[1]!);
        });

        allPurchasesEqual = reduce(zippedPurchasesEqual, (a, b) => {
          return a && b;
        }) as boolean;
      }

      const isSameTicketGroup = oc(ticketGroup).id() === oc(oldTicketGroup).id();

      const isStateChanged = !isEqual(oc(oldTicketGroup).state(), oc(selectedTicketGroup).state());

      const oldSubset = oc(oldTicketGroup).ticketSubset();
      const selectedSubset = oc(selectedTicketGroup).ticketSubset();
      const isSubsetChanged = !every(oldSubset, (val, idx) => {
        if (selectedSubset) {
          return isEqual(
            omit(val, 'lockedByGuestProfile'),
            omit(selectedSubset[idx], 'lockedByGuestProfile')
          );
        }
        return false;
      });

      const oldBlock = oc(oldTicketGroup).block();
      const selectedBlock = oc(selectedTicketGroup).block();
      const isBlockChanged = !every(oldBlock, (val, idx) => {
        if (selectedBlock) {
          return isEqual(val, selectedBlock[idx]);
        }
        return false;
      });

      const hasTicketGroupChangedForCalendarContext =
        isStateChanged || isBlockChanged || isSubsetChanged || !allPurchasesEqual;
      const tainted = isSameTicketGroup && hasTicketGroupChangedForCalendarContext;
      return {
        ...state,
        selectedInventoryItemId,
        calendarItemRequestInProgress: false,
        tainted,
      };
    }
    // Update the day calendar after a Guest tag update to update the priority tag icons
    case SharedAppConstants.PATRON_TAG_UPDATE_DONE:
      return {
        ...state,
        tainted: true,
      };
    case SharedAppConstants.TICKET_GROUP_GET_STARTING:
      if (context) {
        const selectedInventoryItemId: SelectedInventoryItemId = context;
        return {
          ...state,
          selectedInventoryItemId,
          calendarItemRequestInProgress: true,
        };
      }

      return {
        ...state,
        calendarItemRequestInProgress: true,
      };
    case SharedAppConstants.TICKET_GROUP_GET_FAILED:
      return {
        ...state,
        calendarItemRequestInProgress: false,
      };
    case AppConstants.CALENDAR_SET_LOADING: {
      return {
        ...state,
        requestInProgress: payload,
      };
    }
    case AppConstants.MONTH_CALENDAR_FETCH_ALL_STARTING:
    case AppConstants.CALENDAR_ACTION_STARTING:
      return {
        ...state,
        requestInProgress: true,
      };
    case AppConstants.CALENDAR_ACTION_DONE:
    case AppConstants.CALENDAR_ACTION_FAILED: {
      return {
        ...state,
        requestInProgress: false,
      };
    }
    case AppConstants.MONTH_CALENDAR_FETCH_ALL_FAILED:
      return {
        ...state,
        calendar: [],
        monthCalendar: {},
        requestInProgress: false,
      };
    case AppConstants.CALENDAR_CLEAR_DATA_FROM_STORE: {
      return {
        ...state,
        calendar: [],
        monthCalendar: {},
      };
    }
    case AppConstants.CALENDAR_SET_MOVING_TABLE:
      return {
        ...state,
        calendarItemToMove: payload.calendarItemToMove,
        dateTimeToMove: payload.dateTimeToMove,
      };

    case AppConstants.CALENDAR_STOP_MOVING_TABLE:
      return {
        ...state,
        calendarItemToMove: undefined,
        dateTimeToMove: undefined,
      };
    case AppConstants.CALENDAR_SET_CALENDAR_ITEM_TO_HOLD:
      return {
        ...state,
        calendarItemToHold: payload,
      };
    case AppConstants.CALENDAR_SET_SELECTED_TABLE_ID: {
      return {
        ...state,
        selectedTableId: payload,
      };
    }
    case CalendarPopoverActionTypes.CALENDAR_POPOVER_OPEN: {
      return {
        ...state,
        popoverType: payload,
      };
    }
    case CalendarPopoverActionTypes.CALENDAR_POPOVER_CLOSE: {
      return {
        ...state,
        popoverType: undefined,
      };
    }

    case AppConstants.CALENDAR_SET_FOCUSED_TIME_SPAN: {
      return {
        ...state,
        focusedTimeSpan: payload,
      };
    }

    case AppConstants.CALENDAR_RESET_FOCUSED_TIME_SPAN: {
      return {
        ...state,
        focusedTimeSpan: undefined,
      };
    }
    default:
      return state;
  }
}
